import { useMemo, useState, KeyboardEvent, useRef, useEffect } from 'react'
import clsx from 'clsx'

import { RadialBackground } from '@/ui/RadialBackground'
import { Input } from '@/ui/Input'
import { Loader } from '@/ui/Loader'
import { filterSuggestedOptions } from '../helpers/filterSuggestedOptions'
import { useReaderLogStore } from '../store'
import { useGetLogSuggestions } from '../api/useGetLogSuggestions'
import { SuggestionOption } from '@/types/reader'

import styles from './FindControllerLog.module.scss'

import PreviousSearchesIcon from '../assets/previous-searches.svg'

interface SuggestedProps {
    highlightedIndex: number | null
    setItemRef: (el: HTMLButtonElement | null, index: number) => void
    onSelect: (option: SuggestionOption) => void
}

export const FindControllerLog = () => {
    const [search, setSearch] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

    const { data: suggestions, isLoading } = useGetLogSuggestions()
    const { setHistory, setOption } = useReaderLogStore()

    const filteredOptions = useMemo(() => {
        if (!suggestions?.options) {
            return []
        }

        return filterSuggestedOptions(suggestions.options, search)
    }, [search, suggestions])

    const handleSelectOption = (option: SuggestionOption) => {
        setOption(option)
        setHistory(option)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedIndex((prev) => (prev === null || prev === filteredOptions.length - 1 ? 0 : prev + 1))
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedIndex((prev) => (prev === null || prev === 0 ? filteredOptions.length - 1 : prev - 1))
        }
        if (e.key === 'Enter' && (highlightedIndex !== null || filteredOptions.length === 1)) {
            e.preventDefault()
            const option = filteredOptions[highlightedIndex || 0]

            handleSelectOption(option)
        }
    }

    useEffect(() => {
        if (highlightedIndex !== null && itemRefs.current[highlightedIndex]) {
            itemRefs.current[highlightedIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        }
    }, [highlightedIndex])

    return (
        <RadialBackground className={styles.background} variant="main">
            <section className={styles.root}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>Find a controller log</h1>
                    </div>
                    {isLoading && <Loader />}
                    {suggestions && (
                        <>
                            <div className={styles.searchContainer}>
                                <div className={styles.inputContainer}>
                                    <Input
                                        fullWidth
                                        placeholder="Type to search controller logs"
                                        allowClear
                                        hidePlaceholderOnFocus
                                        onChange={(event) => setSearch(event.target.value)}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                                {!search ? (
                                    <Suggested
                                        highlightedIndex={highlightedIndex}
                                        setItemRef={(el, suggestedIndex) => (itemRefs.current[suggestedIndex] = el)}
                                        onSelect={handleSelectOption}
                                    />
                                ) : (
                                    <div className={styles.listContainer}>
                                        <div className={styles.list}>
                                            {filteredOptions.map((item, index) => (
                                                <button
                                                    key={`${item.type}-${index}`}
                                                    ref={(el) => (itemRefs.current[index] = el)}
                                                    type="button"
                                                    className={clsx(styles.item, {
                                                        [styles.highlighted]: highlightedIndex === index,
                                                    })}
                                                    onClick={() => handleSelectOption(item)}
                                                >
                                                    <span>{item.title}</span>
                                                    <span className={styles.hint}>{item.type}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </RadialBackground>
    )
}

const Suggested = ({ highlightedIndex, onSelect, setItemRef }: SuggestedProps) => {
    const { data: suggestions } = useGetLogSuggestions()
    const { history } = useReaderLogStore()

    const isHistoryItemsDisplayed = !!history.length
    const options = isHistoryItemsDisplayed ? history : suggestions?.suggestions || []

    if (!suggestions) {
        return null
    }

    return (
        <div className={styles.suggestionContainer}>
            {!isHistoryItemsDisplayed && <p className={styles.suggestionTitle}>Suggested</p>}
            {options.map((item, index) => (
                <button
                    key={`${item.type}-${index}`}
                    ref={(el) => setItemRef(el, index)}
                    type="button"
                    className={clsx(styles.item, {
                        [styles.highlighted]: highlightedIndex === index,
                        [styles.historyItem]: isHistoryItemsDisplayed,
                    })}
                    onClick={() => onSelect(item)}
                >
                    <div className={styles.itemLabel}>
                        {isHistoryItemsDisplayed && <PreviousSearchesIcon />}
                        <span>{item.title}</span>
                    </div>
                    <span className={styles.hint}>{item.type}</span>
                </button>
            ))}
        </div>
    )
}
